import { ApiError, kindForStatus, type ApiErrorDetail } from "./errors";

/** The cookie the backend writes for double-submit CSRF. It is deliberately
 *  readable by JavaScript; the session cookie itself is HttpOnly and is never
 *  touched here. */
const CSRF_COOKIE = "optiq_admin_csrf";
const CSRF_HEADER = "X-CSRF-Token";

/** Relative on purpose: the app talks to whichever origin served it, so there
 *  is no backend URL anywhere in application source. */
const BASE = "/v1";

export function readCsrfToken(): string | null {
  // document.cookie is a flat string; split rather than regex so a token
  // containing regex-significant characters cannot break parsing.
  for (const part of document.cookie.split(";")) {
    const raw = part.trim();
    const eq = raw.indexOf("=");
    if (eq === -1) continue;
    if (raw.slice(0, eq) === CSRF_COOKIE) {
      return decodeURIComponent(raw.slice(eq + 1));
    }
  }
  return null;
}

type Method = "GET" | "POST" | "PATCH" | "DELETE";

interface RequestOptions {
  method?: Method;
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** Extra request headers.
   *
   * Exists for one thing: `Idempotency-Key`, which the admin order transition
   * requires. It is deliberately not a named parameter — a client that grew a
   * dedicated `idempotencyKey` argument would tempt the next caller to invent
   * one, and the header is the backend's contract, not this function's. */
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = `${BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/** Read a response body without letting a malformed one mask the real status. */
async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    // A non-JSON body (a proxy error page, say) is data, not something to show
    // the administrator verbatim.
    return null;
  }
}

function toDetail(body: unknown): ApiErrorDetail | null {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail: unknown }).detail;
    if (detail && typeof detail === "object") return detail as ApiErrorDetail;
    if (typeof detail === "string") return { message: detail };
  }
  return null;
}

/**
 * The single door to the backend.
 *
 * Every request carries cookies, every mutation carries the CSRF header, and
 * every failure becomes an ApiError with the backend's structured payload
 * intact. Nothing here retries: a publish or an archive that appears to have
 * failed may in fact have succeeded, and repeating it automatically is how a
 * product gets published twice.
 */
export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {};

  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (method !== "GET") {
    const csrf = readCsrfToken();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  // Last, so a caller cannot accidentally drop the CSRF token by supplying its
  // own header map — but also so nothing here silently overrides one either:
  // the two sets are disjoint in every caller, and a collision would be a bug
  // worth seeing rather than one worth papering over.
  Object.assign(headers, options.headers);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      // The session cookie is what authenticates every call.
      credentials: "include",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (cause) {
    // Includes DNS failure, connection refused and abort. The message is
    // deliberately generic — a raw fetch error can name internal hosts.
    throw new ApiError("network", 0, "network request failed", null);
  }

  const body = await parseBody(response);

  if (!response.ok) {
    const detail = toDetail(body);
    throw new ApiError(
      kindForStatus(response.status),
      response.status,
      detail?.message ?? `request failed with status ${response.status}`,
      detail,
    );
  }

  return body as T;
}

/**
 * A POST whose body is a file rather than JSON.
 *
 * Only bulk product import needs one, and it needs one for a reason: a
 * workbook is bytes, and base64-ing it through a JSON field would inflate it by
 * a third and hide its size from every bound the backend puts on it.
 *
 * `Content-Type` is deliberately NOT set — the browser writes it, including the
 * multipart boundary, which cannot be written by hand. Everything else is the
 * ordinary request: cookies, the CSRF header, and the same structured errors.
 */
export async function requestForm<T>(
  path: string,
  form: FormData,
  headers: Record<string, string> = {},
): Promise<T> {
  const csrf = readCsrfToken();
  const merged: Record<string, string> = { ...headers };
  if (csrf) merged[CSRF_HEADER] = csrf;

  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method: "POST",
      headers: merged,
      credentials: "include",
      body: form,
    });
  } catch {
    throw new ApiError("network", 0, "network request failed", null);
  }

  const body = await parseBody(response);
  if (!response.ok) {
    const detail = toDetail(body);
    throw new ApiError(
      kindForStatus(response.status),
      response.status,
      detail?.message ?? `request failed with status ${response.status}`,
      detail,
    );
  }
  return body as T;
}

/** A GET that answers with a file. Used for the template and for export. */
export async function requestFile(
  path: string,
  query?: RequestOptions["query"],
): Promise<{ blob: Blob; filename: string; headers: Headers }> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method: "GET",
      credentials: "include",
    });
  } catch {
    throw new ApiError("network", 0, "network request failed", null);
  }
  if (!response.ok) {
    const detail = toDetail(await parseBody(response));
    throw new ApiError(
      kindForStatus(response.status),
      response.status,
      detail?.message ?? `request failed with status ${response.status}`,
      detail,
    );
  }
  const disposition = response.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    blob: await response.blob(),
    filename: match?.[1] ?? "download.xlsx",
    headers: response.headers,
  };
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { method: "GET", query, signal }),
  post: <T>(path: string, body?: unknown, headers?: Record<string, string>) =>
    request<T>(path, { method: "POST", body, headers }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "PATCH", body }),
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body }),
};
